import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Trash2, AlertTriangle } from "lucide-react";
import type { Deck } from "@/lib/api/deck-api";
import { fetchAdminDecks, deleteAdminDeck } from "@/lib/api/deck-api";
import { toast } from "sonner";

export function AdminDeckManagement() {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    async function loadDecks() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminDecks();
        setDecks(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load decks");
      } finally {
        setLoading(false);
      }
    }

    loadDecks();
  }, []);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this deck? This action cannot be undone.")) {
      return;
    }

    setDeletingId(id);
    try {
      await deleteAdminDeck(id);
      setDecks(prev => prev.filter(deck => deck.id !== id));
      toast.success("Deck deleted successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete deck");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredDecks = decks.filter(deck => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      deck.name.toLowerCase().includes(query) ||
      deck.division_descriptor.toLowerCase().includes(query) ||
      deck.discord_id.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Decks (Admin View)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search decks by name, division, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDecks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No decks found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDecks.map((deck) => (
                  <TableRow key={deck.id}>
                    <TableCell className="font-mono text-xs">{deck.id}</TableCell>
                    <TableCell className="font-medium">{deck.name}</TableCell>
                    <TableCell className="text-sm">{deck.division_descriptor}</TableCell>
                    <TableCell className="font-mono text-xs">{deck.discord_id}</TableCell>
                    <TableCell>
                      {deck.is_public ? (
                        <Badge variant="default">Public</Badge>
                      ) : (
                        <Badge variant="secondary">Private</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {deck.isOutdated && (
                        <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                          <AlertTriangle className="h-3 w-3" />
                          Outdated
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(deck.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(deck.id)}
                        disabled={deletingId === deck.id}
                        className="text-destructive hover:text-destructive"
                      >
                        {deletingId === deck.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}









